import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import AdminSlidingService from "./AdminSlidingService";
import AdminStoryService from "./AdminStoryService";
import { ADMIN_SLIDING_BROWSE_PATH } from "./adminSlidingRoutes";
import { DIFF_LEVELS, templateForGridAndLevel, uniqueGridSizesSorted } from "./adminSlidingUi";

function pickImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const u =
        story.imageUrl ??
        story.image_url ??
        story.imagePreviewUrl ??
        story.image_preview_url ??
        story.imageIcon400Url ??
        story.image_icon_400_url ??
        story.imageIcon200Url ??
        story.image_icon_200_url;
    return typeof u === "string" && u.trim() ? u.trim() : null;
}

/** Full-resolution story illustration (`imageUrl`), not preview/icon variants. */
function pickOriginalImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const u = story.imageUrl ?? story.image_url;
    if (typeof u === "string" && u.trim()) return u.trim();
    return pickImageUrl(story);
}

function toPlayCount(v) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function playEmailsFromStory(story, camel, snake) {
    if (!story || typeof story !== "object") return [];
    const raw = story[camel] ?? story[snake];
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
        const email = typeof item === "string" && item.trim() ? item.trim() : "No email";
        if (seen.has(email)) continue;
        seen.add(email);
        out.push(email);
    }
    return out;
}

function PlayEmailList({ emails }) {
    if (!emails.length) return null;
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.15, mt: 0.5, ml: 0.25 }}>
            {emails.map((email) => (
                <Typography
                    key={email}
                    variant="caption"
                    color={email === "No email" ? "text.disabled" : "text.secondary"}
                    sx={{ wordBreak: "break-all" }}
                >
                    {email}
                </Typography>
            ))}
        </Box>
    );
}

/** StoryRo may use `english`/`french` or legacy `en`/`fr`. */
function storyTitle(story) {
    if (!story || typeof story !== "object") return "";
    const en =
        story.english?.title ??
        story.en?.title ??
        (typeof story.english === "string" ? story.english : null);
    const fr =
        story.french?.title ??
        story.fr?.title ??
        (typeof story.french === "string" ? story.french : null);
    const enStr = typeof en === "string" ? en.trim() : "";
    const frStr = typeof fr === "string" ? fr.trim() : "";
    if (enStr) return enStr;
    if (frStr) return frStr;
    return "";
}

const SECTION_LABEL = {
    EASY: "Easy",
    MEDIUM: "Medium",
    HARD: "Hard",
};

/** All known states for the state dropdown. */
const SLIDING_STATES = ["DRAFT", "REVIEWED", "PUBLISHED", "ARCHIVED"];

/**
 * Parse a raw startingLayout string (comma/space-separated integers) into a number[].
 * Returns null when the string is empty, or an array (possibly with NaN entries if malformed).
 */
function parseLayoutString(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    return s.split(/[\s,]+/).filter(Boolean).map(Number);
}

function layoutStringValid(raw, gridSize) {
    const arr = parseLayoutString(raw);
    if (!arr) return true; // empty = no change, valid
    const n = Number(gridSize) > 0 ? Number(gridSize) * Number(gridSize) : 0;
    if (n === 0) return arr.every((v) => Number.isFinite(v));
    return arr.length === n && arr.every((v) => Number.isFinite(v));
}

/**
 * Edit dialog for a single SlidingRo template.
 * Calls PUT /v1/puzzle/sliding with the changed fields.
 * `storySliding` is the bundle row `{ id, free, publishedAt }` — free is read/written at bundle level.
 */
function EditSlidingTemplateDialog({ open, onClose, template, storySliding, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    // Controlled fields — initialised from template / storySliding when dialog opens
    const [storyId, setStoryId] = useState("");
    const [gridSize, setGridSize] = useState("");
    const [difficultyLevel, setDifficultyLevel] = useState("");
    const [state, setState] = useState("");
    const [free, setFree] = useState(false);
    const [layoutStr, setLayoutStr] = useState("");
    const [makeWeakAgo, setMakeWeakAgo] = useState(false);

    useEffect(() => {
        if (!open || !template) return;
        setStoryId(template.story?.id ?? template.storyId ?? "");
        setGridSize(template.gridSize != null ? String(template.gridSize) : "");
        setDifficultyLevel(template.difficultyLevel ?? "");
        setState(template.state ?? "");
        // free is a bundle-level flag on storySliding; fall back to per-template for legacy data
        const bundleFree = storySliding != null ? storySliding.free === true : template.free === true;
        setFree(bundleFree);
        setLayoutStr(
            Array.isArray(template.startingLayout) ? template.startingLayout.join(", ") : ""
        );
        setMakeWeakAgo(false);
        setSaving(false);
    }, [open, template, storySliding]);

    const resolvedGridSize = Number(gridSize) > 0 ? Number(gridSize) : template?.gridSize ?? 0;
    const layoutValid = layoutStringValid(layoutStr, resolvedGridSize);

    const handleSave = useCallback(async () => {
        if (!template?.id) return;
        setSaving(true);
        const fields = { id: template.id };
        const trimmedStoryId = storyId.trim();
        if (trimmedStoryId && trimmedStoryId !== (template.story?.id ?? template.storyId ?? "")) {
            fields.storyId = trimmedStoryId;
        }
        const gs = Number(gridSize);
        if (Number.isFinite(gs) && gs > 0 && gs !== template.gridSize) {
            fields.gridSize = gs;
        }
        if (difficultyLevel && difficultyLevel !== template.difficultyLevel) {
            fields.difficultyLevel = difficultyLevel;
        }
        if (!makeWeakAgo && state && state !== template.state) {
            fields.state = state;
        }
        // Compare against bundle-level free (storySliding), not per-template
        const currentFree = storySliding != null ? storySliding.free === true : template.free === true;
        if (free !== currentFree) {
            fields.free = free;
        }
        const parsedLayout = parseLayoutString(layoutStr);
        if (parsedLayout && parsedLayout.length > 0) {
            fields.startingLayout = parsedLayout;
        }

        const result = await AdminSlidingService.updateTemplate(fields, makeWeakAgo);
        setSaving(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Save failed" });
            return;
        }
        onSaved(result.data);
        onClose();
    }, [template, storySliding, storyId, gridSize, difficultyLevel, state, free, layoutStr, makeWeakAgo, onSaved, onClose]);

    if (!template) return null;

    return (
        <>
            <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit template
                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", mt: 0.25 }}>
                        {template.id}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Story ID (move to another story)"
                            value={storyId}
                            onChange={(e) => setStoryId(e.target.value)}
                            disabled={saving}
                            fullWidth
                            size="small"
                            helperText="Leave unchanged to keep current story"
                        />
                        <TextField
                            label="Grid size"
                            type="number"
                            value={gridSize}
                            onChange={(e) => setGridSize(e.target.value)}
                            disabled={saving}
                            fullWidth
                            size="small"
                            inputProps={{ min: 2, max: 20 }}
                        />
                        <FormControl fullWidth size="small" disabled={saving}>
                            <InputLabel>Difficulty level</InputLabel>
                            <Select
                                value={difficultyLevel}
                                label="Difficulty level"
                                onChange={(e) => setDifficultyLevel(e.target.value)}
                            >
                                <MenuItem value="">— no change —</MenuItem>
                                {DIFF_LEVELS.map((d) => (
                                    <MenuItem key={d} value={d}>{d}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" disabled={saving || makeWeakAgo}>
                            <InputLabel>State</InputLabel>
                            <Select
                                value={state}
                                label="State"
                                onChange={(e) => setState(e.target.value)}
                            >
                                <MenuItem value="">— no change —</MenuItem>
                                {SLIDING_STATES.map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={free}
                                    onChange={(_, v) => setFree(v)}
                                    disabled={saving}
                                    size="small"
                                />
                            }
                            label="Free (updates all templates under this story)"
                        />
                        <TextField
                            label="Starting layout (comma-separated integers)"
                            value={layoutStr}
                            onChange={(e) => setLayoutStr(e.target.value)}
                            disabled={saving}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            error={!layoutValid}
                            helperText={
                                !layoutValid
                                    ? `Expected ${resolvedGridSize * resolvedGridSize} integers`
                                    : "Leave empty to keep current layout"
                            }
                        />
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={makeWeakAgo}
                                        onChange={(_, v) => setMakeWeakAgo(v)}
                                        disabled={saving}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" component="span">
                                            Make weak ago
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Forces state → PUBLISHED and sets publishedAt to a random weekday
                                            09:00–17:00 (America/Montreal) in the next 7 days. Ignores the State field above.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || !layoutValid}
                    >
                        {saving ? <CircularProgress size={20} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

/** Difficulties that actually have a template row for this grid size. */
function existingLevelsForGrid(templates, gridSize) {
    return DIFF_LEVELS.filter((level) => templateForGridAndLevel(templates, gridSize, level));
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.25;

function clampZoom(value) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));
}

function OriginalImageViewer({ imageUrl, alt }) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef(null);
    const viewportRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const attachedWheelRef = useRef(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setDragging(false);
        dragRef.current = null;
    }, [imageUrl]);

    wheelHandlerRef.current = (event) => {
        event.preventDefault();
        const direction = event.deltaY < 0 ? 1 : -1;
        setZoom((current) => clampZoom(current + direction * ZOOM_STEP));
    };

    const setViewportNode = useCallback((node) => {
        if (viewportRef.current && attachedWheelRef.current) {
            viewportRef.current.removeEventListener("wheel", attachedWheelRef.current);
        }
        viewportRef.current = node;
        if (node) {
            const handler = (event) => {
                wheelHandlerRef.current?.(event);
            };
            attachedWheelRef.current = handler;
            node.addEventListener("wheel", handler, { passive: false });
        } else {
            attachedWheelRef.current = null;
        }
    }, []);

    const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        setDragging(true);
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        setPan({
            x: drag.panX + (event.clientX - drag.startX),
            y: drag.panY + (event.clientY - drag.startY),
        });
    };

    const endDrag = (event) => {
        if (dragRef.current?.pointerId === event.pointerId) {
            dragRef.current = null;
            setDragging(false);
        }
    };

    const resetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                    Original image
                </Typography>
                <Tooltip title="Zoom out">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Zoom out"
                            disabled={zoom <= ZOOM_MIN}
                            onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}
                            sx={{ width: 44, height: 44 }}
                        >
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="caption" sx={{ minWidth: 44, textAlign: "center" }}>
                    {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Zoom in"
                            disabled={zoom >= ZOOM_MAX}
                            onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}
                            sx={{ width: 44, height: 44 }}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Reset zoom">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Reset zoom"
                            disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                            onClick={resetView}
                            sx={{ width: 44, height: 44 }}
                        >
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            <Box
                ref={setViewportNode}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onDoubleClick={resetView}
                sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    overflow: "hidden",
                    cursor: dragging ? "grabbing" : "grab",
                    touchAction: "none",
                    userSelect: "none",
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt={alt}
                    referrerPolicy="no-referrer"
                    draggable={false}
                    sx={{
                        display: "block",
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: "center center",
                        pointerEvents: "none",
                    }}
                />
            </Box>
        </Box>
    );
}

export default function AdminSlidingStoryDetail() {
    const { storyId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    /** Template currently open in the edit dialog */
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [publishing, setPublishing] = useState(false);
    const [previewGenerating, setPreviewGenerating] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [storyDetail, setStoryDetail] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [disabling, setDisabling] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!storyId) {
                setLoading(false);
                setError("Missing story id");
                setErrorCode(null);
                setGroup(null);
                return;
            }
            setLoading(true);
            setGroup(null);
            setError(null);
            setErrorCode(null);
            setStoryDetail(null);

            const [groupResult, storyResult] = await Promise.all([
                AdminSlidingService.getGroupByStoryId(storyId),
                AdminStoryService.getById(storyId),
            ]);
            if (cancelled) return;

            setLoading(false);
            if (!groupResult.success) {
                setError(groupResult.error || "Failed to load");
                setErrorCode(groupResult.code ?? null);
                return;
            }
            setGroup(groupResult.data);
            if (storyResult.success && storyResult.data) {
                setStoryDetail(storyResult.data);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    /** After a successful save, merge the updated SlidingRo back into the group and reload. */
    const handleTemplateSaved = useCallback(
        async (updatedRo) => {
            // Optimistically patch the template in state so the UI updates immediately
            if (updatedRo && updatedRo.id) {
                setGroup((prev) => {
                    if (!prev) return prev;
                    const templates = Array.isArray(prev.templates) ? prev.templates : [];
                    const idx = templates.findIndex((t) => t.id === updatedRo.id);
                    if (idx < 0) return prev;
                    const next = [...templates];
                    next[idx] = { ...templates[idx], ...updatedRo };
                    return { ...prev, templates: next };
                });
            }
            // Full reload to pick up any story/bundle changes (storyId move, free, publishedAt)
            const result = await AdminSlidingService.getGroupByStoryId(storyId);
            if (result.success && result.data) {
                setGroup(result.data);
            }
            setSnackbar({ open: true, severity: "success", message: "Template saved" });
        },
        [storyId]
    );

    const story = group?.story ?? null;
    const templates = group?.templates ?? [];
    const storySliding = group?.storySliding ?? null;
    const bundleState = String(storySliding?.state || "").toUpperCase();
    const canDisable = bundleState === "PUBLISHED";
    const isUnavailable = bundleState === "DISABLED" || bundleState === "DISCARDED";
    const originalImageUrl = useMemo(() => pickOriginalImageUrl(story), [story]);
    const title = useMemo(() => storyTitle(story), [story]);
    const gridSizesSorted = useMemo(() => uniqueGridSizesSorted(templates), [templates]);

    const slidingPlayCount = useMemo(
        () =>
            storyDetail
                ? toPlayCount(storyDetail.slidingPlayCount ?? storyDetail.sliding_play_count)
                : 0,
        [storyDetail],
    );
    const slidingPlayEmails = useMemo(
        () =>
            storyDetail
                ? playEmailsFromStory(storyDetail, "slidingPlayEmails", "sliding_play_emails")
                : [],
        [storyDetail],
    );
    const hasLinkedWordPuzzle = Boolean(
        storyDetail?.crossTpl ||
            storyDetail?.crosswordTpl ||
            storyDetail?.gridTpl ||
            storyDetail?.crosswordTplId ||
            storyDetail?.gridTplId,
    );
    const hasLinkedJigsaw = Boolean(storyDetail?.jigsaw);

    const handleDeleteClick = useCallback(() => {
        if (!storyId) return;
        setDeleteDialogOpen(true);
    }, [storyId]);

    const handleConfirmDelete = useCallback(
        async (id) => {
            if (!id) return;
            setDeleting(true);
            const result = await AdminSlidingService.removeByStoryId(id);
            setDeleting(false);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            navigate(ADMIN_SLIDING_BROWSE_PATH);
        },
        [navigate],
    );

    const handlePublish = useCallback(async () => {
        if (!templates.length) return;
        setPublishing(true);
        try {
            for (const tpl of templates) {
                await AdminSlidingService.updateTemplate({ id: tpl.id, state: "PUBLISHED" }, true);
            }
            // Reload
            const result = await AdminSlidingService.getGroupByStoryId(storyId);
            if (result.success && result.data) {
                setGroup(result.data);
            }
            setSnackbar({ open: true, severity: "success", message: "Published successfully" });
        } catch (e) {
            setSnackbar({ open: true, severity: "error", message: e?.message || "Publish failed" });
        } finally {
            setPublishing(false);
        }
    }, [templates, storyId]);

    const handleConfirmDisable = useCallback(async () => {
        if (!storyId || disabling) return;
        setDisabling(true);
        const result = await AdminSlidingService.disableStory(storyId);
        setDisabling(false);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Disable failed",
            });
            return;
        }
        setSnackbar({
            open: true,
            severity: "success",
            message: "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.",
        });
        const reload = await AdminSlidingService.getGroupByStoryId(storyId);
        if (reload.success && reload.data) {
            setGroup(reload.data);
        }
    }, [storyId, disabling]);

    const handleGenPreview = useCallback(async () => {
        if (!storyId) return;
        setPreviewGenerating(true);
        setPreviewResult(null);
        try {
            const result = await AdminSlidingService.genPreview(storyId);
            if (!result.success) {
                setPreviewResult({ error: result.error || "Failed to generate preview" });
            } else {
                const items = [...(Array.isArray(result.data?.items) ? result.data.items : [])];
                const diffOrder = { EASY: 0, MEDIUM: 1, HARD: 2 };
                items.sort((a, b) => {
                    const gs = (a.gridSize ?? 0) - (b.gridSize ?? 0);
                    if (gs !== 0) return gs;
                    const da = diffOrder[String(a.difficultyLevel || "").toUpperCase()] ?? 99;
                    const db = diffOrder[String(b.difficultyLevel || "").toUpperCase()] ?? 99;
                    return da - db;
                });
                setPreviewResult({ items });
            }
            setPreviewDialogOpen(true);
        } catch (e) {
            setPreviewResult({ error: e?.message || "Failed to generate preview" });
            setPreviewDialogOpen(true);
        } finally {
            setPreviewGenerating(false);
        }
    }, [storyId]);

    const closePreviewDialog = useCallback(() => {
        setPreviewDialogOpen(false);
    }, []);

    return (
        <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ADMIN_SLIDING_BROWSE_PATH)}
                sx={{ mb: 2 }}
            >
                Back to sliding list
            </Button>

            <Typography variant="h5" gutterBottom>
                Sliding puzzle
            </Typography>

            {storyId ? (
                <Typography
                    variant="caption"
                    sx={{ mb: 2, fontFamily: "monospace", wordBreak: "break-all", display: "block" }}
                    color="text.secondary"
                >
                    GET /v1/puzzle/sliding/{storyId}/story
                </Typography>
            ) : null}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {!loading && error ? (
                <Typography color={errorCode === "NO_DATA" ? "text.secondary" : "error"} sx={{ py: 2 }}>
                    {error}
                </Typography>
            ) : null}

            {!loading && !error && group ? (
                <>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 1 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Play records
                            </Typography>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleDeleteClick}
                                disabled={deleting || !storyId}
                                startIcon={
                                    deleting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <DeleteOutlineIcon />
                                    )
                                }
                                sx={{ textTransform: "none", minHeight: 44 }}
                            >
                                {deleting ? "Deleting…" : "Delete"}
                            </Button>
                        </Stack>
                        <Box>
                            <Chip
                                size="small"
                                variant={slidingPlayCount > 0 ? "filled" : "outlined"}
                                color={slidingPlayCount > 0 ? "warning" : "default"}
                                label={`Sliding plays: ${slidingPlayCount}`}
                            />
                            {slidingPlayCount > 0 ? (
                                <PlayEmailList emails={slidingPlayEmails} />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    No play history for this sliding puzzle.
                                </Typography>
                            )}
                        </Box>
                    </Paper>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
                    >
                        {title ? (
                            <Typography variant="h6" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                                {title}
                            </Typography>
                        ) : null}
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleGenPreview}
                                disabled={previewGenerating || !storyId}
                                startIcon={
                                    previewGenerating ? <CircularProgress size={16} color="inherit" /> : null
                                }
                                sx={{ textTransform: "none" }}
                            >
                                {previewGenerating ? "Generating…" : "Generate Preview"}
                            </Button>
                            {canDisable ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setDisableDialogOpen(true)}
                                    disabled={disabling || !storyId}
                                    startIcon={
                                        disabling ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <BlockIcon />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        color: "grey.700",
                                        borderColor: "grey.500",
                                        minHeight: 44,
                                    }}
                                >
                                    {disabling ? "Disabling…" : "Disable"}
                                </Button>
                            ) : null}
                        </Stack>
                    </Stack>
                    {story?.language ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {story.language}
                        </Typography>
                    ) : null}

                    {storySliding ? (
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
                            {bundleState ? (
                                <Chip
                                    size="small"
                                    label={bundleState}
                                    color={bundleState === "PUBLISHED" ? "success" : "default"}
                                    sx={
                                        isUnavailable
                                            ? { bgcolor: "grey.400", color: "grey.900", fontWeight: 600 }
                                            : undefined
                                    }
                                />
                            ) : null}
                            <Chip
                                size="small"
                                label={storySliding.free === true ? "Free" : "Paid"}
                                color={storySliding.free === true ? "secondary" : "default"}
                                variant={storySliding.free === true ? "filled" : "outlined"}
                            />
                            {storySliding.publishedAt ? (
                                <Typography variant="caption" color="text.secondary">
                                    Published: {new Date(storySliding.publishedAt).toLocaleString()}
                                </Typography>
                            ) : (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handlePublish}
                                    disabled={publishing || templates.length === 0}
                                    startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : null}
                                    sx={{ textTransform: "none", backgroundColor: "#3BA666", "&:hover": { backgroundColor: "#2e7d32" } }}
                                >
                                    {publishing ? "Publishing…" : "Publish"}
                                </Button>
                            )}
                            <Typography variant="caption" sx={{ fontFamily: "monospace" }} color="text.disabled">
                                bundle: {storySliding.id}
                            </Typography>
                        </Stack>
                    ) : null}

                    {gridSizesSorted.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No templates with a grid size found for this story.
                        </Typography>
                    ) : (
                        <Stack
                            direction="row"
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            spacing={3}
                            sx={{ mb: 2 }}
                        >
                            {gridSizesSorted.map((gs) => {
                                const levels = existingLevelsForGrid(templates, gs);
                                if (levels.length === 0) return null;
                                return (
                                    <Stack
                                        key={gs}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                        flexWrap="wrap"
                                        useFlexGap
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {gs}×{gs}
                                        </Typography>
                                        {levels.map((level) => {
                                            const tpl = templateForGridAndLevel(templates, gs, level);
                                            if (!tpl) return null;
                                            const label = SECTION_LABEL[level] ?? level;
                                            return (
                                                <Tooltip
                                                    key={`${gs}-${level}`}
                                                    title={`Edit ${label} ${gs}×${gs}`}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={0.75}
                                                        onClick={() => setEditingTemplate(tpl)}
                                                        sx={{
                                                            cursor: "pointer",
                                                            minHeight: 44,
                                                        }}
                                                    >
                                                        <Typography variant="body2">{label}</Typography>
                                                        <CheckCircleIcon
                                                            sx={{ color: "#3BA666", fontSize: 22 }}
                                                            aria-label={`${label} ${gs}×${gs} exists`}
                                                        />
                                                    </Stack>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}

                    {originalImageUrl ? (
                        <Box sx={{ mb: 3, width: "100%" }}>
                            <OriginalImageViewer
                                imageUrl={originalImageUrl}
                                alt={title ? `${title} — original` : "Story original image"}
                            />
                            <Button
                                component="a"
                                href={originalImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                sx={{ mt: 1, textTransform: "none" }}
                            >
                                Open full image
                            </Button>
                        </Box>
                    ) : null}
                </>
            ) : null}

            <EditSlidingTemplateDialog
                open={Boolean(editingTemplate)}
                onClose={() => setEditingTemplate(null)}
                template={editingTemplate}
                storySliding={storySliding}
                onSaved={handleTemplateSaved}
            />

            <Dialog open={previewDialogOpen} onClose={closePreviewDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Sliding previews
                    {Array.isArray(previewResult?.items) && previewResult.items.length > 0
                        ? ` (${previewResult.items.length})`
                        : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {previewResult?.error ? (
                        <Typography color="error">{previewResult.error}</Typography>
                    ) : Array.isArray(previewResult?.items) && previewResult.items.length > 0 ? (
                        <Stack spacing={2}>
                            {previewResult.items.map((item) => {
                                const levelKey = String(item.difficultyLevel || "").toUpperCase();
                                const levelLabel = SECTION_LABEL[levelKey] ?? (levelKey || "—");
                                const gs = item.gridSize;
                                const heading =
                                    gs != null && gs > 0
                                        ? `${gs}×${gs} — ${levelLabel}`
                                        : levelLabel;
                                return (
                                    <Paper key={item.slidingId || item.s3Key || heading} variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            {heading}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all", mb: 1 }}
                                        >
                                            slidingId: {item.slidingId || "—"}
                                        </Typography>
                                        {item.previewUrl ? (
                                            <Box
                                                component="img"
                                                src={item.previewUrl}
                                                alt={heading}
                                                sx={{
                                                    width: "100%",
                                                    maxHeight: 360,
                                                    objectFit: "contain",
                                                    display: "block",
                                                    borderRadius: 1,
                                                    bgcolor: "action.hover",
                                                    mb: 1,
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                No preview URL
                                            </Typography>
                                        )}
                                        {item.s3Key ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    s3Key:
                                                </Box>{" "}
                                                {item.s3Key}
                                            </Typography>
                                        ) : null}
                                        {item.previewUrl ? (
                                            <Button
                                                component="a"
                                                href={item.previewUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                                sx={{ mt: 1, textTransform: "none" }}
                                            >
                                                Open in new tab
                                            </Button>
                                        ) : null}
                                    </Paper>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">No preview items returned.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreviewDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded. Linked jigsaw and word puzzles for this story are disabled too."
                targetName={title ? `'${title}'` : storyId ? `Story ${storyId}` : "This sliding puzzle"}
                targetId={storyId}
                callback={handleConfirmDisable}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete sliding puzzle?"
                targetName={title || storyId}
                targetId={storyId}
                message={[
                    "This permanently deletes all sliding templates, preview images, and play history for this story.",
                    "Sliding is discarded so scheduled jobs will not recreate it. The story record stays.",
                    hasLinkedWordPuzzle
                        ? "The linked crossword or word-search puzzle is not deleted."
                        : null,
                    hasLinkedJigsaw
                        ? "The jigsaw puzzle for this story is not deleted."
                        : null,
                    "This action cannot be undone.",
                ].filter(Boolean)}
                warningAlert={
                    slidingPlayCount > 0
                        ? `${slidingPlayCount} play record${slidingPlayCount !== 1 ? "s" : ""} will be deleted. Did you check the players who played this puzzle?`
                        : "This action cannot be undone."
                }
                extraContent={
                    slidingPlayCount > 0 ? (
                        <Box sx={{ mt: 2 }}>
                            <Chip
                                size="small"
                                variant="filled"
                                color="warning"
                                label={`Sliding plays: ${slidingPlayCount}`}
                            />
                            <PlayEmailList emails={slidingPlayEmails} />
                        </Box>
                    ) : null
                }
                callback={handleConfirmDelete}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
